import { Injectable } from '@angular/core';

import { RewardPoints } from '@frontend/sports/types/models/loyalty';

import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2StandardPick } from '../../../core/picks/betslip-v2-standard-pick';
import { BetslipV2HorseRaceOptionMarketPick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { IFailedSlipResult, IPlacedBetslipResult, ISucceededSlipResult, SlipResultStatus } from '../../betplacement/models';
import { BetPlacementErrorIcon } from '../../validation/errors/bet-placement-error-icon';
import { BetslipError } from '../../validation/errors/betslip-error';
import { ResultError } from '../../validation/errors/result/result-error';
import { isNewStakeHintError } from '../../validation/services/utils/stake-limit-errors-info-utils';
import { IQuickBetPlaceResultState, IQuickBetPlaceResultWarning, defaultQuickBetPlaceResultState } from '../quick-bet.state';

interface PickWithErrors<T extends BetslipPick = BetslipPick> {
    pick: T;
    errors: ResultError[];
}

type PickErrorInfo = Pick<IQuickBetPlaceResultState, 'errors' | 'warnings' | 'isSuccessful'>;

@Injectable({
    providedIn: 'root',
})
export class QuickBetPlacementResultProcessorService {
    getBetPlaceResultState(betPlacementResult: IPlacedBetslipResult): IQuickBetPlaceResultState {
        const quickBetPlaceResultState = {
            ...defaultQuickBetPlaceResultState,
            errors: [],
            warnings: [],
        };

        // QuickBet supports only a single slip, be it single or combo
        const slip = betPlacementResult.slips[0];

        return slip.status === SlipResultStatus.Success
            ? this.handleSuccessfulSlip(quickBetPlaceResultState, slip as ISucceededSlipResult, betPlacementResult.rewardPoints)
            : this.handleFailedSlip(quickBetPlaceResultState, slip);
    }

    handleSuccessfulSlip(state: IQuickBetPlaceResultState, successfulBetslip: ISucceededSlipResult, rewardPoints?: RewardPoints) {
        state.isSuccessful = true;
        state.slipResults = successfulBetslip;
        state.rewardPoints = rewardPoints;
        //	If no possible winnings, it's a SP, ignore odds.
        if (successfulBetslip.possibleWinnings) {
            state.acceptedOdds = successfulBetslip.picks[0]!.odds;
        }
        if (successfulBetslip.boostedOdds) {
            state.boostedOdds = successfulBetslip.boostedOdds;
        }
        state.betNumber = successfulBetslip.betNumber;

        return state;
    }

    private handleFailedSlip(state: IQuickBetPlaceResultState, failedSlip: IFailedSlipResult): IQuickBetPlaceResultState {
        const slipErrors = failedSlip.errors;
        if (slipErrors?.length) {
            state.errors = slipErrors.filter((e) => e.icon === BetPlacementErrorIcon.Error && !e.newStakeHint);
            state.warnings = slipErrors
                .filter((e) => e.icon === BetPlacementErrorIcon.Warning || e.newStakeHint)
                .map((err) => ({
                    disableButton: !isNewStakeHintError(err),
                    error: err,
                }));

            return state;
        }

        const failedPicks = Object.values(failedSlip.picks);

        return failedPicks.reduce((resultState, failedPick) => {
            const pickErrorsInfo = this.pickToBetPlacedResult({ pick: failedPick.clientPick, errors: failedPick.errors });

            resultState.isSuccessful &&= pickErrorsInfo.isSuccessful;
            resultState.errors.push(...pickErrorsInfo.errors);
            resultState.warnings.push(...pickErrorsInfo.warnings);

            return resultState;
        }, state);
    }

    private pickToBetPlacedResult(pickWithErrors: PickWithErrors): PickErrorInfo {
        if (this.isSportPick(pickWithErrors)) {
            return this.getResultForSportPick(pickWithErrors);
        }

        if (this.isBetBuilderPick(pickWithErrors)) {
            return this.getResultForBetBuilderPick(pickWithErrors);
        }

        if (this.isParticipantPick(pickWithErrors)) {
            return this.getResultForRacePick(pickWithErrors);
        }

        throw new Error('Not supported pick type');
    }

    private isSportPick(pickWithErrors: PickWithErrors): pickWithErrors is PickWithErrors<BetslipV2OptionMarketPick> {
        const pick = pickWithErrors.pick;

        return pick instanceof BetslipV1Pick || pick instanceof BetslipV2StandardPick || pick instanceof BetslipV2HorseRaceOptionMarketPick;
    }

    private isBetBuilderPick(pickWithErrors: PickWithErrors): pickWithErrors is PickWithErrors<BetslipBetBuilderPick> {
        const pick = pickWithErrors.pick;

        return pick instanceof BetslipBetBuilderPick;
    }

    private isParticipantPick(pickWithErrors: PickWithErrors): pickWithErrors is PickWithErrors<BetslipV2ParticipantPick> {
        const pick = pickWithErrors.pick;

        return pick instanceof BetslipV2ParticipantPick;
    }

    private getBaseResultState(pickWithErrors: PickWithErrors): PickErrorInfo {
        return {
            isSuccessful: false,
            errors: this.filterErrorsByType(pickWithErrors.errors),
            warnings: pickWithErrors.pick.isAvailable ? this.filterWarnings(pickWithErrors.errors) : [],
        };
    }

    private getResultForSportPick(pickWithErrors: PickWithErrors<BetslipV2OptionMarketPick>): PickErrorInfo {
        return this.getBaseResultState(pickWithErrors);
    }

    private getResultForBetBuilderPick(pickWithErrors: PickWithErrors<BetslipBetBuilderPick>): PickErrorInfo {
        return {
            ...this.getBaseResultState(pickWithErrors),
            isSuccessful: !pickWithErrors.errors?.length,
        };
    }

    private getResultForRacePick(pickWithErrors: PickWithErrors<BetslipV2ParticipantPick>): PickErrorInfo {
        return this.getBaseResultState(pickWithErrors);
    }

    private filterErrorsByType(errors: ResultError[]): BetslipError[] {
        if (!errors?.length) {
            return [];
        }

        return errors.filter((e) => e.icon === BetPlacementErrorIcon.Error);
    }

    private filterWarnings(errors: ResultError[]): IQuickBetPlaceResultWarning[] {
        if (!errors?.length) {
            return [];
        }

        return errors
            .filter((error) => error.icon === BetPlacementErrorIcon.Warning)
            .map((error) => ({
                disableButton: false,
                error,
            }));
    }
}
